'use client';

import { useRef, useEffect, useCallback, useState } from 'react';

interface PongProps {
  onExit: () => void;
}

export function Pong({ onExit }: PongProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const keysRef = useRef<Set<string>>(new Set());
  const [score, setScore] = useState({ player: 0, cpu: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState('');

  const WINNING_SCORE = 5;
  const WIDTH = 600;
  const HEIGHT = 320;
  const PADDLE_W = 10;
  const PADDLE_H = 60;
  const BALL_SIZE = 8;
  const PADDLE_SPEED = 5;
  const CPU_SPEED = 3.2;

  const stateRef = useRef({
    playerY: HEIGHT / 2 - PADDLE_H / 2,
    cpuY: HEIGHT / 2 - PADDLE_H / 2,
    ballX: WIDTH / 2,
    ballY: HEIGHT / 2,
    ballVX: 4,
    ballVY: 2.5,
    playerScore: 0,
    cpuScore: 0,
  });

  const resetBall = useCallback(() => {
    const s = stateRef.current;
    s.ballX = WIDTH / 2;
    s.ballY = HEIGHT / 2;
    s.ballVX = (Math.random() > 0.5 ? 1 : -1) * 4;
    s.ballVY = (Math.random() - 0.5) * 5;
  }, []);

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const s = stateRef.current;
    const keys = keysRef.current;

    // Player movement
    if (keys.has('ArrowUp') || keys.has('w')) {
      s.playerY = Math.max(0, s.playerY - PADDLE_SPEED);
    }
    if (keys.has('ArrowDown') || keys.has('s')) {
      s.playerY = Math.min(HEIGHT - PADDLE_H, s.playerY + PADDLE_SPEED);
    }

    // CPU AI — tracks ball with slight delay
    const cpuCenter = s.cpuY + PADDLE_H / 2;
    const diff = s.ballY - cpuCenter;
    if (Math.abs(diff) > 10) {
      s.cpuY += Math.sign(diff) * CPU_SPEED;
    }
    s.cpuY = Math.max(0, Math.min(HEIGHT - PADDLE_H, s.cpuY));

    // Ball movement
    s.ballX += s.ballVX;
    s.ballY += s.ballVY;

    // Top/bottom bounce
    if (s.ballY <= 0 || s.ballY >= HEIGHT - BALL_SIZE) {
      s.ballVY *= -1;
      s.ballY = Math.max(0, Math.min(HEIGHT - BALL_SIZE, s.ballY));
    }

    // Player paddle collision (left side)
    if (
      s.ballX <= 20 + PADDLE_W &&
      s.ballX >= 20 &&
      s.ballY + BALL_SIZE >= s.playerY &&
      s.ballY <= s.playerY + PADDLE_H
    ) {
      s.ballVX = Math.abs(s.ballVX) * 1.05;
      const hitPos = (s.ballY - s.playerY) / PADDLE_H - 0.5;
      s.ballVY = hitPos * 8;
      s.ballX = 20 + PADDLE_W + 1;
    }

    // CPU paddle collision (right side)
    if (
      s.ballX + BALL_SIZE >= WIDTH - 20 - PADDLE_W &&
      s.ballX <= WIDTH - 20 &&
      s.ballY + BALL_SIZE >= s.cpuY &&
      s.ballY <= s.cpuY + PADDLE_H
    ) {
      s.ballVX = -Math.abs(s.ballVX) * 1.05;
      const hitPos = (s.ballY - s.cpuY) / PADDLE_H - 0.5;
      s.ballVY = hitPos * 8;
      s.ballX = WIDTH - 20 - PADDLE_W - BALL_SIZE - 1;
    }

    // Cap ball speed
    const maxSpeed = 10;
    s.ballVX = Math.max(-maxSpeed, Math.min(maxSpeed, s.ballVX));
    s.ballVY = Math.max(-maxSpeed, Math.min(maxSpeed, s.ballVY));

    // Score
    if (s.ballX < 0) {
      s.cpuScore++;
      setScore({ player: s.playerScore, cpu: s.cpuScore });
      if (s.cpuScore >= WINNING_SCORE) {
        setGameOver(true);
        setWinner('CPU');
        return;
      }
      resetBall();
    }
    if (s.ballX > WIDTH) {
      s.playerScore++;
      setScore({ player: s.playerScore, cpu: s.cpuScore });
      if (s.playerScore >= WINNING_SCORE) {
        setGameOver(true);
        setWinner('YOU');
        return;
      }
      resetBall();
    }

    // Draw
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // Center dashed line
    ctx.setLineDash([6, 6]);
    ctx.strokeStyle = '#00ff4133';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(WIDTH / 2, 0);
    ctx.lineTo(WIDTH / 2, HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);

    // Paddles
    ctx.fillStyle = '#00ff41';
    ctx.shadowColor = '#00ff41';
    ctx.shadowBlur = 10;
    ctx.fillRect(20, s.playerY, PADDLE_W, PADDLE_H);
    ctx.fillRect(WIDTH - 20 - PADDLE_W, s.cpuY, PADDLE_W, PADDLE_H);

    // Ball
    ctx.shadowBlur = 15;
    ctx.fillRect(s.ballX, s.ballY, BALL_SIZE, BALL_SIZE);
    ctx.shadowBlur = 0;

    // Score display
    ctx.fillStyle = '#00ff4166';
    ctx.font = '32px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(String(s.playerScore), WIDTH / 2 - 50, 40);
    ctx.fillText(String(s.cpuScore), WIDTH / 2 + 50, 40);

    animRef.current = requestAnimationFrame(gameLoop);
  }, [resetBall]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'w', 's'].includes(e.key)) {
        e.preventDefault();
        keysRef.current.add(e.key);
      }
      if (e.key === 'Escape' || e.key === 'q') {
        onExit();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    animRef.current = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      cancelAnimationFrame(animRef.current);
    };
  }, [gameLoop, onExit]);

  return (
    <div className="flex flex-col items-center gap-2 my-2 fade-in">
      <div className="text-muted text-xs">
        ↑/↓ or W/S to move &nbsp;|&nbsp; First to {WINNING_SCORE} wins &nbsp;|&nbsp; ESC or Q to quit
      </div>
      <div className="border border-term/30 rounded" style={{ lineHeight: 0 }}>
        <canvas
          ref={canvasRef}
          width={WIDTH}
          height={HEIGHT}
          className="block"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>
      {gameOver && (
        <div className="text-center">
          <div className={`text-lg font-bold ${winner === 'YOU' ? 'text-term text-glow' : 'text-danger text-glow-danger'}`}>
            {winner === 'YOU' ? '🏆 YOU WIN!' : 'CPU WINS'}
          </div>
          <div className="text-muted text-xs mt-1">
            {winner === 'YOU'
              ? 'Nice reflexes. Now channel that into the swarm.'
              : 'Better luck next time. Maybe stick to random walks.'}
          </div>
          <div className="text-muted text-xs mt-1">Press ESC or Q to return</div>
        </div>
      )}
    </div>
  );
}
